import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '../context/CartContext';
import { signup } from '../services/api';
import sambasLogo from '../assets/sambas-logo.png';
import './CartDrawer.css';

function CartDrawer() {
  const { 
    cartItems, 
    cartTotal, 
    isCartOpen, 
    closeCart, 
    removeFromCart, 
    updateQuantity,
    clearCart 
  } = useCart();

  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleCheckout = () => {
    setShowCheckoutPopup(true);
    setSubmitStatus(null);
    setEmail('');
  };

  const handleClosePopup = () => {
    setShowCheckoutPopup(false);
    setSubmitStatus(null);
    setEmail('');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Detect what products are in the cart
      const hasCandy = cartItems.some(item => item.product === 'Performance Candies');
      const hasApparel = cartItems.some(item => 
        item.product.toLowerCase().includes('shirt') || 
        item.product.toLowerCase().includes('sambas –')
      );
      let product = 'candy';
      if (hasCandy && hasApparel) product = 'candy & apparel';
      else if (hasApparel) product = 'apparel';

      await signup.create(email, product);
      setSubmitStatus('success');
      setEmail('');
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('already')) {
        setSubmitStatus('duplicate');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="cart-backdrop" onClick={closeCart} />
      
      {/* Drawer */}
      <div className="cart-drawer">
        <div className="cart-drawer-header">
          <h2>Your Cart</h2>
          <button className="cart-close-btn" onClick={closeCart}>
            ✕
          </button>
        </div>

        <div className="cart-drawer-content">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon">🛒</div>
              <p>Your cart is empty</p>
              <button className="cart-continue-btn" onClick={closeCart}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-info">
                      <h3>{item.product}</h3>
                      <p className="cart-item-details">
                        {item.size} • {item.packLabel}
                      </p>
                      <p className="cart-item-price">${item.price.toFixed(2)} each</p>
                    </div>
                    
                    <div className="cart-item-actions">
                      <div className="quantity-controls">
                        <button 
                          className="qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button 
                          className="qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <p className="cart-item-total">${item.totalPrice.toFixed(2)}</p>
                      <button 
                        className="cart-item-remove"
                        onClick={() => removeFromCart(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="cart-total">
                  <span>Total</span>
                  <span className="cart-total-amount">${cartTotal.toFixed(2)}</span>
                </div>
                
                <button className="cart-checkout-btn" onClick={handleCheckout}>
                  Checkout
                </button>
                
                <button className="cart-clear-btn" onClick={clearCart}>
                  Clear Cart
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Checkout Popup Modal - Rendered via Portal */}
      {showCheckoutPopup && createPortal(
        <>
          <div className="checkout-popup-backdrop" onClick={handleClosePopup} />
          <div className="checkout-popup">
            <button className="checkout-popup-close" onClick={handleClosePopup}>
              ✕
            </button>
            
            {submitStatus === 'success' ? (
              <div className="checkout-popup-success">
                <div className="success-icon">✓</div>
                <h2>You're on the list!</h2>
                <p>We'll notify you when we're back in stock with your 10% discount code.</p>
                <button className="checkout-popup-done-btn" onClick={handleClosePopup}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="checkout-popup-icon">
                  <img src={sambasLogo} alt="Sambas Energy" className="checkout-popup-logo" />
                </div>
                <h2>Sorry, we're out of stock!</h2>
                <p>Sign up to get notified for <strong>10% off</strong> your first order!</p>
                
                <form onSubmit={handleEmailSubmit} className="checkout-popup-form">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="checkout-popup-input"
                    required
                  />
                  <button 
                    type="submit" 
                    className="checkout-popup-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Signing up...' : 'Notify Me'}
                  </button>
                </form>
                
                {submitStatus === 'duplicate' && (
                  <p className="checkout-popup-error" style={{ color: '#ffa500' }}>You're already registered! We'll notify you when we're back.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="checkout-popup-error">Something went wrong. Please try again.</p>
                )}
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export default CartDrawer;
